import { useState, useMemo, useEffect, useCallback } from "react";
import { useModel } from '../ModelProvider';
import OnboardingIncomplete from '../components/OnboardingIncomplete';
import ModelLoadError from '../components/ModelLoadError';
import ViewOnly from '../components/ViewOnly';
import GuardedDialog, { useSnapshotDirty } from '../components/GuardedDialog';
import { getFixedAssets, createFixedAsset, updateFixedAsset, deleteFixedAsset as apiDeleteFixed, getIntangibleAssets, createIntangibleAsset, updateIntangibleAsset, deleteIntangibleAsset as apiDeleteIntang, getOnboardingState, updateOnboardingState } from '../api';
import { filterForecast, groupByYear, getYear, formatMonthLabel } from '../engines/dateUtils';
// ★ A2 (Principle 0) — THE D&A MATH LIVES IN THE ENGINE, ONCE. This page used to carry its
// own byte-copy of assetMonthlySchedule + computeSchedule; computeDA has always exported
// both (computeDA.js:56 and :121), so the copy was pure duplication. It is deleted: the
// schedule the operator READS is now produced by the same function the model BOOKS from,
// and a fix to the SL/DDB/SYD math can no longer reach one and miss the other.
import { assetMonthlySchedule, computeSchedule, partitionBySubType, toMonth, depreciationStartMonth } from '../engines/computeDA';
// The D&A start convention (2026-10-13): labels, the stored key and the save payload have ONE owner; the month arithmetic
// is the engine's depreciationStartMonth (imported above) -- this page adds no "+1" of its own.
import { DA_START_OPTIONS, DA_START_FIELD_LABEL, normalizeDaStart, daStartLabel, daStartFromOnboardingState, daStartSavePayload } from '../utils/daStartConvention';
import { buildAssetBsLineOptions, bsLineLabelMap } from '../utils/assetBsLineOptions';
import { DEP_METHODS } from '../utils/depreciationMethods';
import Spark from '../components/Spark';

/* ═══════════════════════════════════════════════════════════════════════════════
   D&A MODULE — Depreciation & Amortization Schedule
   Straight-line depreciation (Fixed Assets) & amortization (Intangible Assets)
   Flows into IS forecast as "Other Operating Expenses"
   ═══════════════════════════════════════════════════════════════════════════════ */

const r2 = n => Math.round(n * 100) / 100;
const fmt = n => {if(!n||n===0) return "—";const v=r2(n);const s=new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}).format(Math.abs(v));return v<0?`(${s})`:s;};
const fmtK = n => {if(!n||n===0) return "—";const v=r2(n);const a=Math.abs(v);let s;if(a>=1e6) s=`$${(a/1e6).toFixed(2)}M`;else if(a>=1e4) s=`$${(a/1e3).toFixed(1)}K`;else s=new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}).format(a);return v<0?`(${s})`:s;};
const fmtInt = n => {if(!n) return "";const v=Math.round(n);const s="$"+Math.abs(v).toLocaleString("en-US");return v<0?`(${s})`:s;};
const parseDollar = s => parseFloat(String(s).replace(/[$,()]/g,"")) || 0;
let _uid = 1;
const uid = () => `da-${_uid++}-${Date.now()}`;


// ★ B5 K3 (option B) — THE PRIVATE toMonth BYTE-COPY IS DELETED. computeDA has always
// exported toMonth, and this page already imports assetMonthlySchedule / computeSchedule /
// partitionBySubType from it — the same reuse move A2 and s3c already made for this file's
// other copies. It mattered here beyond tidiness: the copy carried its OWN "2026-01"
// fallback, so fixing only the engine would have left the page dating a dateless asset to
// Jan-2026 while the engine skipped it — a divergence created by the very fix meant to
// remove a guess (§6 Principle 0).


/* ═══════════════════════════════════════════════════════════════════════════════
   COMPUTE ENGINE — Supports multiple depreciation/amortization methods
   Straight-line, Double Declining Balance, Sum-of-Years' Digits
   All methods start in the purchase month regardless of day -- or the month after it, when the company's
   "Depreciation starts" setting says so (utils/daStartConvention.js; applied by the engine's depreciationStartMonth)
   ═══════════════════════════════════════════════════════════════════════════════ */
// W-063: DEP_METHODS now lives in utils/depreciationMethods (imported above) -- one list for this page, Step 8 and the
// asset template.

/* ★ A2 — the page's private assetMonthlySchedule + computeSchedule stood here and are
   DELETED. They were a copy of computeDA's exports of the same names: assetMonthlySchedule
   was character-identical, and computeSchedule differed in exactly one way — the engine
   opens with `const MONTHS = allMonths || []` where the copy iterated `allMonths` directly.
   That guard is unreachable from this page (allMonths is `rawData?.allMonths || []` at its
   declaration below), so adopting the engine's version is a strict robustness gain and
   provably not a numeric change — verified over cid=3's 31 real assets × 36 months, 1,739
   schedule cells, zero differences. Both are imported at the top of this file. */

/* ═══════════════════════════════════════════════════════════════════════════════
   ASSET MODAL — Add / Edit fixed or intangible asset
   ═══════════════════════════════════════════════════════════════════════════════ */
function AssetModal({canEdit = false, asset, kind, onSave, onClose, onDelete, depBsLines, amortBsLines, startConvention}) {
  const isNew = !asset;
  // BS-line options for the picker — tenant bsLines of the correct role (fixed_asset for
  // depreciation, intangible_asset for amortization), labelled from bs_actuals_meta. REQUIRED:
  // the asset STORES this routing (computeDA reads it; no guessing). W-063 (rule 92): a NEW asset starts with NO line
  // -- it used to be born on the first line of the list -- and Save waits until one is chosen. This is the
  // SOLE classifier — the old descriptive `type` field was retired (bs_line_id classifies,
  // description describes; type said the same thing a third time).
  const bsLineList = kind === "dep" ? (depBsLines || []) : (amortBsLines || []);
  const defaultLife = kind === "dep" ? 5 : 3;
  const accentColor = kind === "dep" ? "#3D85C6" : "#8B6FC0";

  // W-087 F2 (Brian 2026-10-02): a NEW asset's purchase date starts BLANK and REQUIRED, like the line above -- it used to be
  // born "2026-01-01", a hard-coded month that is already closed for a 2026 client. An existing asset keeps its stored date.
  const [f, setF] = useState(asset || {id:uid(),desc:"",cost:0,date:"",life:defaultLife,method:"SL",salvage:0,bs_line_id:""});

  const mS = {width:"100%",padding:"10px 12px",background:"#F0F1F5",border:"1px solid rgba(0,0,0,0.12)",borderRadius:"8px",color:"#1A1C2E",fontSize:"14px",fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"};
  const lS = {display:"block",fontSize:"11px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"6px"};

  // Dollar input with formatting
  const CostInput = ({field}) => {
    const [ed, setEd] = useState(false);
    const [raw, setRaw] = useState("");
    return <input style={mS}
      value={ed ? raw : fmtInt(f[field])}
      onFocus={() => {setEd(true); setRaw(String(f[field]||""));}}
      onChange={e => setRaw(e.target.value)}
      onBlur={() => {setEd(false); setF(p=>({...p, [field]: parseDollar(raw)}));}}
    />;
  };

  // Preview: compute first 3 years of schedule
  const previewSchedule = f.cost > 0 && f.life > 0 ? assetMonthlySchedule({...f, method:f.method||"SL"}, startConvention) : null;
  const totalMonths = Math.round(f.life * 12);
  const yr1 = previewSchedule ? r2(previewSchedule.monthly.slice(0,12).reduce((s,v)=>s+v,0)) : 0;
  const yr2 = previewSchedule ? r2(previewSchedule.monthly.slice(12,24).reduce((s,v)=>s+v,0)) : 0;
  const yr3 = previewSchedule ? r2(previewSchedule.monthly.slice(24,36).reduce((s,v)=>s+v,0)) : 0;
  const mo1 = previewSchedule && previewSchedule.monthly.length > 0 ? r2(previewSchedule.monthly[0]) : 0;
  // ★★ B5 K3 — THE PAGE NEEDS THE SAME GUARD THE ENGINE GOT, AND HERE IT IS REACHABLE BY
  // AN OPERATOR RATHER THAN ONLY IN THEORY: the date field is a plain <input type="date">
  // (below) and CLEARING it sets f.date to "", so toMonth now returns null WHILE THE
  // OPERATOR IS TYPING. Splitting that null would blow up the modal mid-edit. No date means
  // no schedule window, so both months render as "—" rather than as an invented Jan-2026.
  // The schedule's FIRST month, from the engine's own rule (the purchase month, or the month after it under the company's
  // "month after" convention) -- so FIRST MONTH / LAST MONTH below show what the model will book.
  const startM = depreciationStartMonth(f.date, startConvention);
  const [sy,sm] = startM ? startM.split("-").map(Number) : [null, null];
  const lastActiveM = startM ? new Date(sy, sm - 1 + totalMonths - 1, 1) : null;
  const lastM = lastActiveM ? `${lastActiveM.getFullYear()}-${String(lastActiveM.getMonth()+1).padStart(2,"0")}` : null;

  const dirty = useSnapshotDirty({ f }); // W-032
  return (
    <GuardedDialog isDirty={dirty} onClose={onClose} cardStyle={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.1)",borderRadius:"20px",padding:"32px",width:"520px",maxHeight:"88vh",overflowY:"auto",boxShadow:"0 24px 80px rgba(0,0,0,0.15)"}}>
        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"24px"}}>
          <h3 style={{margin:0,fontSize:"20px",fontWeight:700,color:"#1A1C2E"}}>{isNew ? (kind==="dep"?"Add Fixed Asset":"Add Intangible Asset") : (kind==="dep"?"Edit Fixed Asset":"Edit Intangible Asset")}</h3>
          <div style={{fontSize:"11px",padding:"4px 10px",borderRadius:"6px",background:`${accentColor}15`,color:accentColor,fontWeight:700}}>{kind==="dep"?"Depreciation":"Amortization"}</div>
        </div>

        {/* Form Fields */}
        <ViewOnly canEdit={canEdit}><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"16px"}}>
          <div>
            <label style={lS}>{kind==="dep"?"Depreciation":"Amortization"} Method</label>
            <select style={mS} value={f.method||"SL"} onChange={e=>setF(p=>({...p,method:e.target.value}))}>
              {DEP_METHODS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <div style={{fontSize:"9px",color:"#A0A3B8",marginTop:"4px"}}>{DEP_METHODS.find(m=>m.id===(f.method||"SL"))?.desc}</div>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lS}>Balance Sheet Line</label>
            <select style={mS} value={f.bs_line_id||""} onChange={e=>setF(p=>({...p,bs_line_id:e.target.value}))}>
              <option value="">{"\u2014 choose a line \u2014"}</option>
              {bsLineList.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
            {!f.bs_line_id && <div data-da-mark="required" style={{fontSize:"11px",color:"#B45309",fontWeight:600,marginTop:"4px"}}>Required &mdash; choose a line</div>}
            <div style={{fontSize:"9px",color:"#A0A3B8",marginTop:"4px"}}>Which {kind==="dep"?"fixed":"intangible"}-asset line this rolls onto (BS + Cash Flow). Asset Type above is descriptive only.</div>
          </div>
          <div style={{gridColumn:"1/-1"}}>
            <label style={lS}>Description</label>
            <input style={mS} value={f.desc} onChange={e=>setF(p=>({...p,desc:e.target.value}))} placeholder="e.g., Macbook Pro for new hire"/>
          </div>
          <div>
            <label style={lS}>Cost Basis</label>
            <CostInput field="cost"/>
          </div>
          <div>
            <label style={lS}>Purchase Date</label>
            <input type="date" style={mS} value={f.date} onChange={e=>setF(p=>({...p,date:e.target.value}))}/>
            {!f.date && <div data-da-mark="required-date" style={{fontSize:"11px",color:"#B45309",fontWeight:600,marginTop:"4px"}}>Required &mdash; enter the purchase date</div>}
          </div>
          <div>
            <label style={lS}>Useful Life (years)</label>
            <input type="text" inputMode="numeric" style={mS} value={f.life} onChange={e=>{const v=e.target.value;if(v===""||/^\d*\.?\d*$/.test(v)) setF(p=>({...p,life:v===""?0:parseFloat(v)||0}));}}/>
          </div>
          {/* ★ B5 s4 — SALVAGE IS A FIXED-ASSET CONCEPT ONLY. `intangible_assets` has no
              salvage_value column, and saveAmort's payload has never sent one, so for an
              intangible this input was collecting a number the system had nowhere to put.
              Gated on `kind`, NOT on sub_type: a NEW asset's form state carries no sub_type
              (see the useState seed above), so keying on it would hide the field when editing
              and show it again when adding — exactly backwards. `kind` is the modal's own
              discriminator and is passed by all four call sites for both add and edit. */}
          {kind === "dep" && (
          <div>
            <label style={lS}>Salvage Value</label>
            <CostInput field="salvage"/>
            <div style={{fontSize:"9px",color:"#A0A3B8",marginTop:"4px"}}>Residual value at end of life (usually $0)</div>
          </div>
          )}
        </div>

        {/* Schedule Preview */}
        {f.cost > 0 && f.life > 0 && <div style={{marginTop:"20px",padding:"16px",borderRadius:"12px",background:"rgba(0,0,0,0.025)",border:"1px solid rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:"11px",fontWeight:700,color:"#8B8FA3",textTransform:"uppercase",marginBottom:"10px"}}>Schedule Preview — {DEP_METHODS.find(m=>m.id===(f.method||"SL"))?.label}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"12px"}}>
            <div>
              <div style={{fontSize:"9px",color:"#A0A3B8",fontWeight:600}}>FIRST MONTH</div>
              <div style={{fontSize:"14px",fontWeight:700,color:"#1A1C2E"}}>{startM ? formatMonthLabel(startM, "shortYear") : "\u2014"}</div>
            </div>
            <div>
              <div style={{fontSize:"9px",color:"#A0A3B8",fontWeight:600}}>LAST MONTH</div>
              <div style={{fontSize:"14px",fontWeight:700,color:"#1A1C2E"}}>{lastM ? formatMonthLabel(lastM, "shortYear") : "\u2014"}</div>
            </div>
            {/* ★ B5 s4 — hidden for amortization along with the input that feeds it. It reads
                cost − salvage, which for an intangible is always just cost; showing it would
                imply a deduction the operator can no longer make. Hidden entirely rather than
                relabelled (operator decision). */}
            {kind === "dep" && (
            <div>
              <div style={{fontSize:"9px",color:"#A0A3B8",fontWeight:600}}>DEPRECIABLE BASE</div>
              <div style={{fontSize:"14px",fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#1A1C2E"}}>{fmt(r2(f.cost-(f.salvage||0)))}</div>
            </div>
            )}
          </div>
          {/* Year-by-year breakdown */}
          <div style={{marginTop:"12px",display:"grid",gridTemplateColumns:`repeat(${Math.min(Math.ceil(f.life),5)},1fr)`,gap:"8px"}}>
            {Array.from({length:Math.min(Math.ceil(f.life),5)},(_,i)=>{
              const yrAmt = previewSchedule ? r2(previewSchedule.monthly.slice(i*12,(i+1)*12).reduce((s,v)=>s+v,0)) : 0;
              const moAmt = previewSchedule && previewSchedule.monthly[i*12] ? r2(previewSchedule.monthly[i*12]) : 0;
              return <div key={i} style={{padding:"8px",borderRadius:"8px",background:"rgba(0,0,0,0.03)"}}>
                <div style={{fontSize:"9px",color:"#A0A3B8",fontWeight:600}}>YEAR {i+1}</div>
                <div style={{fontSize:"13px",fontWeight:700,fontFamily:"'Space Mono',monospace",color:accentColor}}>{fmtK(yrAmt)}</div>
                <div style={{fontSize:"9px",color:"#A0A3B8"}}>{fmt(moAmt)}/mo</div>
              </div>;
            })}
          </div>
          {f.life > 5 && <div style={{fontSize:"9px",color:"#A0A3B8",marginTop:"6px"}}>+ {Math.ceil(f.life)-5} more years…</div>}
        </div>}</ViewOnly>

        {/* Action Buttons */}
        <div style={{display:"flex",justifyContent:"space-between",marginTop:"24px"}}>
          <div>
            {!isNew && <ViewOnly canEdit={canEdit}><button onClick={()=>onDelete(f.id)} style={{padding:"10px 20px",borderRadius:"10px",border:"1px solid rgba(224,122,95,0.3)",background:"transparent",color:"#E07A5F",cursor:"pointer",fontSize:"13px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Delete Asset</button></ViewOnly>}
          </div>
          <div style={{display:"flex",gap:"10px"}}>
            <button onClick={onClose} style={{padding:"10px 20px",borderRadius:"10px",border:"1px solid rgba(0,0,0,0.12)",background:"transparent",color:"#6B7094",cursor:"pointer",fontSize:"13px",fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>Cancel</button>
            <ViewOnly canEdit={canEdit}><button onClick={()=>onSave(f)} disabled={!f.bs_line_id || !f.date} data-da-save="asset" style={{opacity:(f.bs_line_id && f.date)?1:0.5,padding:"10px 24px",borderRadius:"10px",border:"none",background:`linear-gradient(135deg,${accentColor},${kind==="dep"?"#5A5FC0":"#6B4FA0"})`,color:"#fff",cursor:f.bs_line_id?"pointer":"not-allowed",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:`0 4px 20px ${accentColor}40`}}>{isNew?"Add Asset":"Save Changes"}</button></ViewOnly>
          </div>
        </div>
    </GuardedDialog>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════════════════════════════════════════ */
/* W-047.4: the private Spark copy is DELETED -- this page imports components/Spark.jsx (one sparkline). */

function AssetTable({assets, kind, onEdit, label, cutoff, bsLabels, startConvention}) {
  return <div style={{overflowX:"auto",maxHeight:"420px"}}>
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:"11px"}}>
      <thead><tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)",position:"sticky",top:0,background:"#FFFFFF",zIndex:1}}>
        <th style={{padding:"8px",textAlign:"left",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>BS Line</th>
        <th style={{padding:"8px",textAlign:"left",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>Description</th>
        <th style={{padding:"8px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>Cost Basis</th>
        <th style={{padding:"8px",textAlign:"center",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>Date</th>
        <th style={{padding:"8px",textAlign:"center",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>Life</th>
        <th style={{padding:"8px",textAlign:"center",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>Method</th>
        <th style={{padding:"8px",textAlign:"right",fontSize:"9px",fontWeight:600,color:"#8B8FA3",textTransform:"uppercase"}}>Mo. 1 {label}</th>
      </tr></thead>
      <tbody>
        {assets.map(a => {
          const fc = toMonth(a.date) > cutoff;
          const fontColor = fc ? "#1A1C2E" : "#2A6DB0";
          const sched = assetMonthlySchedule(a, startConvention);
          const mo1 = sched.monthly.length > 0 ? r2(sched.monthly[0]) : 0;
          const methodLabel = (DEP_METHODS.find(m=>m.id===(a.method||"SL"))?.label||"SL").replace("Double Declining Balance","DDB").replace("Sum-of-Years' Digits","SYD").replace("Straight-Line","SL");
          return <tr key={a.id} style={{borderBottom:"1px solid rgba(0,0,0,0.04)",cursor:"pointer"}} onClick={()=>onEdit(a)} onMouseEnter={e=>e.currentTarget.style.background="rgba(0,0,0,0.02)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <td style={{padding:"6px 8px",fontSize:"10px",color:fontColor}}>{(bsLabels && bsLabels[a.bs_line_id]) || a.bs_line_id}</td>
            <td style={{padding:"6px 8px",fontSize:"10px",color:fontColor,maxWidth:"200px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.desc}</td>
            <td style={{padding:"6px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"10px",color:fontColor}}>{fmtK(a.cost)}</td>
            {/* ★ B5 K3 — ML() splits its argument, so a null month would throw. Unreachable
                today (purchase_date is NOT NULL and toFixed/toIntang coerce anyway), but the
                coercion is a separately-filed item and this cell must not become the place
                that discovers its removal. */}
            <td style={{padding:"6px 8px",textAlign:"center",fontSize:"10px",color:fontColor}}>{a.date ? formatMonthLabel(toMonth(a.date), "shortYear") : "\u2014"}</td>
            <td style={{padding:"6px 8px",textAlign:"center",fontSize:"10px",color:fontColor}}>{a.life}yr</td>
            <td style={{padding:"6px 8px",textAlign:"center",fontSize:"9px",color:fontColor}}>{methodLabel}</td>
            <td style={{padding:"6px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"10px",color:fontColor}}>{fmt(mo1)}</td>
          </tr>;
        })}
        <tr style={{borderTop:"2px solid rgba(0,0,0,0.08)",background:"rgba(0,0,0,0.015)"}}>
          <td colSpan={2} style={{padding:"10px 8px",fontWeight:700}}>Total ({assets.length} assets)</td>
          <td style={{padding:"10px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700}}>{fmtK(r2(assets.reduce((s,a)=>r2(s+a.cost),0)))}</td>
          <td colSpan={4}></td>
        </tr>
      </tbody>
    </table>
  </div>;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════════ */
const SL = {position:"sticky",left:0,zIndex:2,background:"#FFFFFF",minWidth:"220px",maxWidth:"220px",fontFamily:"'DM Sans',sans-serif"};

export default function DAModulePage({ companyId }) {
  const { markStale, rawData, cutoff, onboardingGap, modelState, modelError, canEditModel, superAdmin } = useModel();
  const allMonths = useMemo(() => rawData?.allMonths || [], [rawData?.allMonths]);
  const forecast = useMemo(() => filterForecast(allMonths, cutoff), [allMonths, cutoff]);
  const cutoffIdx = useMemo(() => allMonths.indexOf(cutoff), [allMonths, cutoff]);
  const isForecast = useCallback(m => m > cutoff, [cutoff]);
  // Year groups for column-section headers and annual summary rows.
  // allActuals = every month in this year is ≤ cutoff (full-year actuals badge).
  const yearGroups = useMemo(() => {
    const groups = groupByYear(allMonths);
    return Object.keys(groups).map(Number).sort((a, b) => a - b).map(year => {
      const months = groups[year];
      return { year, months, allActuals: months.every(m => m <= cutoff) };
    });
  }, [allMonths, cutoff]);
  // U1 (2026-10-30): the three forecast cards sum the WHOLE forecast, so they name its span ("2026–2027"), as the Vendor and
  // Payroll cards do -- it read the LAST forecast year alone ("2027") over a 16-month sum.
  const forecastYearLabel = useMemo(() => { if (!forecast.length) return ''; const a = getYear(forecast[0]), b = getYear(forecast.at(-1)); return a === b ? String(a) : `${a}\u2013${b}`; }, [forecast]);
  const lastMonth = allMonths.at(-1);
  // BS-line picker options (shared helper — same source ScenariosPage uses). The leaf id the
  // asset routes onto (stored as bs_line_id) is the sole classifier; the descriptive `type` field
  // + its dropdown were retired.
  const bsLineOptions = useMemo(() => buildAssetBsLineOptions(rawData?.bsLines, rawData?.bsActualsMeta), [rawData?.bsLines, rawData?.bsActualsMeta]);
  // id → BS-line label, for the schedule table's "BS Line" column (replaces the old Asset Type col).
  const bsLabelMap = useMemo(() => bsLineLabelMap(bsLineOptions), [bsLineOptions]);
  const [view, setView] = useState("depreciation");
  const [fixedAssets, setFixedAssets] = useState([]);
  const [intangAssets, setIntangAssets] = useState([]);
  const [modal, setModal] = useState(null); // null | "new-dep" | "new-amort" | {asset, kind}
  const [loading, setLoading] = useState(true);
  /* THE D&A START CONVENTION (2026-10-13) -- a COMPANY-wide setting, not a per-asset one: every schedule on this page and in
     the model starts in the in-service month ('in_month', the default) or the month after it ('next_month').
     Read from the onboarding-state row (loadData, below -- this page fetches independently of ModelProvider, as it does for
     the assets); seeded from the model's copy until that read returns. Saved on change by its own PUT, which names only
     this one key. That route is restricted to the Finledom administrator on the server, so the control is rendered for
     the super admin only; everyone else sees the value as text. */
  const [daStart, setDaStart] = useState(() => normalizeDaStart(rawData?.daStartConvention));
  const [daStartSaving, setDaStartSaving] = useState(false);
  const [daStartError, setDaStartError] = useState("");
  const saveDaStart = async (value) => {
    const next = normalizeDaStart(value);
    if (next === daStart || daStartSaving) return;
    setDaStartSaving(true);
    setDaStartError("");
    try {
      await updateOnboardingState(companyId, daStartSavePayload(next));
      setDaStart(next);
      markStale();   // the statements recompute with the new convention on their next read
    } catch (err) {
      // Not saved: the select stays on the stored value (it is controlled by daStart) and the reason is shown beside it.
      setDaStartError("Not saved: " + (err?.response?.data?.error || err?.message || "unknown error"));
    } finally {
      setDaStartSaving(false);
    }
  };

  // Transform DB row to in-memory format: {id, desc, cost, date, life, salvage, method, bs_line_id}
  const toFixed = r => ({
    id: r.id, desc: r.description || "",
    cost: parseFloat(r.cost) || 0, date: r.purchase_date ? String(r.purchase_date).slice(0,10) : "2024-01-01",
    life: r.useful_life_years || 5, salvage: parseFloat(r.salvage_value) || 0,
    method: r.depreciation_method || "SL", bs_line_id: r.bs_line_id || "",
    // ★ B5 s3c — this page FETCHES INDEPENDENTLY of ModelProvider, so ModelProvider's
    // s3a stamp never reaches it. Derive sub_type the same way and from the same fact:
    // which endpoint the row came from. Same rule, same value, applied at each boundary
    // that turns a DB row into an asset object.
    sub_type: 'fixed_assets',
  });
  const toIntang = r => ({
    id: r.id, desc: r.description || "",
    cost: parseFloat(r.cost) || 0, date: r.purchase_date ? String(r.purchase_date).slice(0,10) : "2024-01-01",
    life: r.useful_life_years || 3, method: r.amortization_method || "SL", bs_line_id: r.bs_line_id || "",
    // ★ B5 s3c — see the toFixed mapper above.
    sub_type: 'intangible_assets',
  });

  const loadData = useCallback(async () => {
    try {
      const [fixedRes, intangRes, obRes] = await Promise.all([
        getFixedAssets(companyId),
        getIntangibleAssets(companyId),
        // The D&A start convention lives on the onboarding-state row. A failed read keeps the model's copy (the seed above).
        getOnboardingState(companyId).catch(() => null),
      ]);
      setFixedAssets(fixedRes.data.map(toFixed));
      setIntangAssets(intangRes.data.map(toIntang));
      if (obRes) setDaStart(normalizeDaStart(daStartFromOnboardingState(obRes.data)));
    } catch (err) {
      console.error('Failed to load D&A data:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveDep = async (asset) => {
    try {
      const dbPayload = { description: asset.desc, cost: asset.cost, salvage_value: asset.salvage || 0, purchase_date: asset.date, useful_life_years: asset.life, depreciation_method: asset.method || "SL", bs_line_id: asset.bs_line_id };
      let assetId = asset.id;
      if (typeof assetId === 'number') {
        await updateFixedAsset(companyId, assetId, dbPayload);
      } else {
        const res = await createFixedAsset(companyId, dbPayload);
        assetId = res.data.id;
      }
      const updated = { ...asset, id: assetId };
      setFixedAssets(p => { const i = p.findIndex(a=>a.id===asset.id||a.id===assetId); if(i>=0){const u=[...p];u[i]=updated;return u;} return [...p,updated]; });
    } catch (err) { console.error('Failed to save fixed asset:', err); }
    markStale();
    setModal(null);
  };
  const delDep = async (id) => {
    try { if (typeof id === 'number') await apiDeleteFixed(companyId, id); setFixedAssets(p => p.filter(a=>a.id!==id)); } catch (err) { console.error('Failed to delete fixed asset:', err); }
    markStale();
    setModal(null);
  };
  const saveAmort = async (asset) => {
    try {
      const dbPayload = { description: asset.desc, cost: asset.cost, purchase_date: asset.date, useful_life_years: asset.life, amortization_method: asset.method || "SL", bs_line_id: asset.bs_line_id };
      let assetId = asset.id;
      if (typeof assetId === 'number') {
        await updateIntangibleAsset(companyId, assetId, dbPayload);
      } else {
        const res = await createIntangibleAsset(companyId, dbPayload);
        assetId = res.data.id;
      }
      /* ★★ B5 s4 — THE LOAD-BEARING HALF. `asset` is the modal's form state, so it carries
         whatever salvage was typed before the field was hidden (or arrives from any future
         edit / re-classify path). The dbPayload above already omits salvage_value, so the DB
         never sees it — but this object goes straight into local state, and the page's own
         schedule runs partitionBySubType -> computeSchedule -> assetMonthlySchedule, which
         computes cost − salvage on the AMORTIZATION path too (one shared schedule function).
         MEASURED: a salvage of 5,000 on "Domain Name" drops in-window amortization by
         $2,833.22 across 34 months on cid=3 and cid=8 — visible until the next reload, then
         silently gone, because toIntang omits salvage and the refetch restores 0.
         Forcing 0 here makes local state agree with what the DB will return. Hiding the input
         is not enough on its own: a hidden field prevents nothing, it only stops showing. */
      const updated = { ...asset, id: assetId, salvage: 0 };
      setIntangAssets(p => { const i = p.findIndex(a=>a.id===asset.id||a.id===assetId); if(i>=0){const u=[...p];u[i]=updated;return u;} return [...p,updated]; });
    } catch (err) { console.error('Failed to save intangible asset:', err); }
    markStale();
    setModal(null);
  };
  const delAmort = async (id) => {
    try { if (typeof id === 'number') await apiDeleteIntang(companyId, id); setIntangAssets(p => p.filter(a=>a.id!==id)); } catch (err) { console.error('Failed to delete intangible asset:', err); }
    markStale();
    setModal(null);
  };

  /* ★★ B5 s3c — THE DISPLAY PATH READS sub_type, VIA THE ENGINE'S OWN PARTITION.
   *
   * This used to be `computeSchedule(fixedAssets)` and `computeSchedule(intangAssets)` —
   * the same decide-by-argument-position contract the engine retired in s3b. Two places
   * answering "is this fixed or intangible?" two different ways is the §6 divergence B5
   * exists to close, and a page that disagreed with the engine would show one D&A split
   * while the statements showed another.
   *
   * ⓘ IT IMPORTS partitionBySubType RATHER THAN RE-READING THE FIELD ITSELF. A local
   *   `filter(a => a.sub_type === ...)` here would be a second implementation of the same
   *   rule — closing one divergence by opening another. Reusing the function also brings
   *   its fail-loud warn along, identically worded, from one place.
   *
   * ⓘ DISPLAY ONLY: `dep`/`amort` feed the scorecard totals, the sparklines and the table
   *   cells on this page. Nothing here reaches runFullModel — the model recomputes from
   *   the DB after markStale(). So this moves no stored number and no golden can see it. */
  const daBuckets = useMemo(
    () => partitionBySubType(fixedAssets, intangAssets),
    [fixedAssets, intangAssets]
  );
  const dep = useMemo(() => computeSchedule(daBuckets.depreciating, allMonths, daStart), [daBuckets, allMonths, daStart]);
  const amort = useMemo(() => computeSchedule(daBuckets.amortizing, allMonths, daStart), [daBuckets, allMonths, daStart]);

  const depForecastTotal = r2(forecast.reduce((s, m) => r2(s + (dep[m]?.expense || 0)), 0));
  const amortForecastTotal = r2(forecast.reduce((s, m) => r2(s + (amort[m]?.expense || 0)), 0));
  const totalDAForecast = r2(depForecastTotal + amortForecastTotal);
  const depLastMonth = (lastMonth && dep[lastMonth]?.expense) || 0;
  const amortLastMonth = (lastMonth && amort[lastMonth]?.expense) || 0;

  const pill = a => ({padding:"7px 16px",borderRadius:"8px",fontSize:"12px",fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",border:a?"none":"1px solid rgba(0,0,0,0.1)",background:a?"linear-gradient(135deg,#3D85C6,#5A5FC0)":"transparent",color:a?"#fff":"#6B7094",transition:"all 0.2s"});

  /* ★ CRASHROOT STEP 2 — this page now reads `modelState`, the provider's single
     terminal-exit answer. Until this build it read NEITHER modelState NOR
     onboardingGap, so a computed-but-HONESTLY-EMPTY tenant (no forecast horizon, or
     no model window) and a THROWN fetch alike cleared this page's own `loading` with
     allMonths===[] — and it spun FOREVER. Both hangs were reproduced on the pre-fix
     tree: diffs/_crashroot2-verifyA-pre.txt.
     'empty' REUSES the existing OnboardingIncomplete JSX (the gap object still names
     the copy); 'error' is the SHARED ModelLoadError added in step 1 — one
     implementation, imported, never an error <div> per page (Principle 0).
     ⛔ THE EXISTING GUARD BELOW IS KEPT, AND STAYS BELOW THESE RETURNS. Belt AND
        braces: this page's `loading` is its OWN flag, which no provider change
        touches; and above these returns the guard would send a named-empty tenant
        straight back to a spinner. */
  if (modelState === 'error') return <ModelLoadError message={modelError} />;
  if (modelState === 'empty') return <OnboardingIncomplete gap={onboardingGap} />;

  /* THE GATE NOW COVERS THE EMPTY WINDOW, NOT JUST `loading`. loading===false does NOT
     imply rawData!==null: ModelProvider.ensureFresh() returns early when the cutoff is not
     yet seeded, so it RESOLVES WITHOUT COMPUTING and this page renders with allMonths===[].
     `allMonths` is bound at :285 (rawData?.allMonths || []), well above this line. */
  if (modelState === 'loading' || loading || !allMonths.length) return <div style={{padding:"60px",textAlign:"center",color:"#8B8FA3",fontFamily:"'DM Sans',sans-serif"}}>Loading D&A data...</div>;

  return <div style={{fontFamily:"'DM Sans',sans-serif",background:"#F4F5F7",minHeight:"100vh",padding:"24px"}}>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet"/>

    {/* Header */}
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"24px"}}>
      <div>
        <div style={{fontSize:"22px",fontWeight:700,color:"#1A1C2E",letterSpacing:"-0.5px"}}>Depreciation & Amortization</div>
        <div style={{fontSize:"12px",color:"#8B8FA3",marginTop:"2px"}}>
          <span>{allMonths.length ? `${allMonths.length} months (${formatMonthLabel(allMonths[0], "shortYear")} – ${formatMonthLabel(lastMonth, "shortYear")})` : "—"}</span>
          <span style={{margin:"0 8px"}}>·</span>
          <span><span style={{color:"#2A6DB0"}}>Actuals in blue</span>, <span style={{color:"#1A1C2E"}}>forecast in black</span></span>
          <span style={{margin:"0 8px"}}>·</span>
          <span>Forecast flows → IS "Other Operating Expenses"</span>
        </div>
      </div>
      <div style={{display:"flex",gap:"6px"}}>
        {[["depreciation","Depreciation"],["amortization","Amortization"],["summary","IS Feed"]].map(([k,l])=>
          <button key={k} onClick={()=>setView(k)} style={pill(view===k)}>{l}</button>
        )}
      </div>
    </div>

    {/* The D&A start convention -- one company-wide setting (utils/daStartConvention.js). Editable by the super admin
        (the server restricts its route to the Finledom administrator); read-only text for everyone else. */}
    <div data-da-start="row" style={{display:"flex",alignItems:"center",gap:"10px",flexWrap:"wrap",background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"12px",padding:"12px 18px",marginBottom:"20px",fontSize:"12px",color:"#1A1C2E"}}>
      <span style={{fontWeight:700}}>{DA_START_FIELD_LABEL}:</span>
      {superAdmin
        ? <ViewOnly canEdit={canEditModel}><select data-da-start="select" value={daStart} disabled={daStartSaving} onChange={e=>saveDaStart(e.target.value)} style={{padding:"6px 10px",background:"#F0F1F5",border:"1px solid rgba(0,0,0,0.12)",borderRadius:"8px",color:"#1A1C2E",fontSize:"12px",fontFamily:"'DM Sans',sans-serif"}}>
            {DA_START_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select></ViewOnly>
        : <span data-da-start="value" style={{fontWeight:600}}>{daStartLabel(daStart)}</span>}
      <span style={{color:"#8B8FA3"}}>Applies to every asset of this company, amortization included. The cost of an asset is still added to the balance sheet in its purchase month.{superAdmin ? " Saved when changed." : " Set by the Finledom administrator."}</span>
      {daStartSaving && <span style={{color:"#8B8FA3"}}>Saving...</span>}
      {daStartError && <span data-da-start="error" style={{color:"#B45309",fontWeight:600}}>{daStartError}</span>}
    </div>

    {/* KPI Cards */}
    <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"16px",marginBottom:"24px"}}>
      {[
        [`${forecastYearLabel} Total D&A`, fmtK(totalDAForecast), "#E07A5F", allMonths.map(m => r2((dep[m]?.expense||0)+(amort[m]?.expense||0))), "#E07A5F"],
        [`${forecastYearLabel} Depreciation`, fmtK(depForecastTotal), "#3D85C6", allMonths.map(m => dep[m]?.expense||0), "#3D85C6"],
        [`${forecastYearLabel} Amortization`, fmtK(amortForecastTotal), "#8B6FC0", allMonths.map(m => amort[m]?.expense||0), "#8B6FC0"],
        [`${lastMonth ? formatMonthLabel(lastMonth, "shortYear") : "—"} Monthly D&A`, fmtK(r2(depLastMonth+amortLastMonth)), "#1A1C2E"],
      ].map(([label, val, color, sparkData, sparkColor], idx) =>
        <div key={idx} style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"14px",padding:"18px"}}>
          <div style={{fontSize:"10px",fontWeight:700,color:"#8B8FA3",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"8px"}}>{label}</div>
          <div style={{fontSize:"22px",fontWeight:700,color,fontFamily:"'Space Mono',monospace"}}>{val}</div>
          {sparkData && <div style={{marginTop:"10px"}}><Spark data={sparkData} color={sparkColor} w={180} h={28} divIdx={cutoffIdx}/></div>}
        </div>
      )}
    </div>

    {/* ═══ DEPRECIATION TAB ═══ */}
    {view==="depreciation"&&<div>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px",marginBottom:"20px"}}>
        <div style={{fontSize:"14px",fontWeight:700,color:"#1A1C2E",marginBottom:"16px"}}>Monthly Depreciation Schedule</div>
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",fontSize:"10px",minWidth:"100%"}}>
            <thead>
              <tr>
                <th style={{...SL,padding:"6px 16px",textAlign:"left",fontSize:"10px",fontWeight:700,color:"#1A1C2E"}}>Line Item</th>
                {yearGroups.map(({year,months,allActuals},i)=>{const isLast=i===yearGroups.length-1;const drawBoundary=!isLast&&months[months.length-1]===cutoff;return <th key={year} colSpan={months.length} style={{padding:"6px",textAlign:"center",fontSize:"10px",fontWeight:700,color:allActuals?"#A0A3B8":"#3D85C6",background:allActuals?"#FAFAFA":undefined,borderRight:drawBoundary?"2px solid rgba(61,133,198,0.3)":"none"}}>{year}{allActuals?" — ACTUALS (derived)":""}</th>;})}
              </tr>
              <tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)"}}>
                <th style={{...SL,padding:"8px 16px"}}></th>
                {allMonths.map((m,i)=><th key={m} style={{padding:"8px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:isForecast(m)?"#1A1C2E":"#2A6DB0",whiteSpace:"nowrap",background:isForecast(m)?"#FFFFFF":"#FAFAFA",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.3)":"none"}}>{formatMonthLabel(m, "shortYear")}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{...SL,padding:"8px 16px",fontWeight:700,fontSize:"11px",color:"#3D85C6"}}>Depreciation Expense</td>
                {allMonths.map((m,i)=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"10px",color:isForecast(m)?"#1A1C2E":"#2A6DB0",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(dep[m]?.expense||0)}</td>)}
              </tr>
              <tr style={{borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                <td style={{...SL,padding:"8px 16px",fontSize:"10px",color:"#8B8FA3"}}>Accumulated Depreciation</td>
                {allMonths.map((m,i)=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"9px",color:isForecast(m)?"#8B8FA3":"#A0A3B8",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(dep[m]?.accumulated||0)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{fontSize:"11px",color:"#B7950B",marginTop:"12px",maxWidth:"680px"}}>Historical (closed) months are a derived straight-line schedule computed from the asset records and may not tie to booked GL actuals — the booked depreciation in the Income Statement governs for closed months. Only the forecast months feed the model.</div>
      </div>

      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"16px"}}>
          <div>
            <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E"}}>Fixed Assets — Depreciation Schedule</div>
            <div style={{fontSize:"12px",color:"#8B8FA3"}}>Straight-line: Cost Basis ÷ (Useful Life × 12). Click any row to edit.</div>
          </div>
          <ViewOnly canEdit={canEditModel}><button onClick={()=>setModal("new-dep")} style={{padding:"9px 22px",borderRadius:"10px",border:"none",background:"linear-gradient(135deg,#3D85C6,#5A5FC0)",color:"#fff",cursor:"pointer",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(61,133,198,0.25)"}}>+ Add Fixed Asset</button></ViewOnly>
        </div>
        <AssetTable assets={fixedAssets} kind="dep" label="Dep." onEdit={a=>setModal({asset:a,kind:"dep"})} cutoff={cutoff} bsLabels={bsLabelMap} startConvention={daStart}/>
      </div>
    </div>}

    {/* ═══ AMORTIZATION TAB ═══ */}
    {view==="amortization"&&<div>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px",marginBottom:"20px"}}>
        <div style={{fontSize:"14px",fontWeight:700,color:"#1A1C2E",marginBottom:"16px"}}>Monthly Amortization Schedule</div>
        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",fontSize:"10px",minWidth:"100%"}}>
            <thead>
              <tr>
                <th style={{...SL,padding:"6px 16px",textAlign:"left",fontSize:"10px",fontWeight:700,color:"#1A1C2E"}}>Line Item</th>
                {yearGroups.map(({year,months,allActuals},i)=>{const isLast=i===yearGroups.length-1;const drawBoundary=!isLast&&months[months.length-1]===cutoff;return <th key={year} colSpan={months.length} style={{padding:"6px",textAlign:"center",fontSize:"10px",fontWeight:700,color:allActuals?"#A0A3B8":"#8B6FC0",background:allActuals?"#FAFAFA":undefined,borderRight:drawBoundary?"2px solid rgba(61,133,198,0.3)":"none"}}>{year}{allActuals?" — ACTUALS (derived)":""}</th>;})}
              </tr>
              <tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)"}}>
                <th style={{...SL,padding:"8px 16px"}}></th>
                {allMonths.map((m,i)=><th key={m} style={{padding:"8px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:isForecast(m)?"#1A1C2E":"#2A6DB0",whiteSpace:"nowrap",background:isForecast(m)?"#FFFFFF":"#FAFAFA",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.3)":"none"}}>{formatMonthLabel(m, "shortYear")}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{...SL,padding:"8px 16px",fontWeight:700,fontSize:"11px",color:"#8B6FC0"}}>Amortization Expense</td>
                {allMonths.map((m,i)=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"10px",color:isForecast(m)?"#1A1C2E":"#2A6DB0",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(amort[m]?.expense||0)}</td>)}
              </tr>
              <tr style={{borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                <td style={{...SL,padding:"8px 16px",fontSize:"10px",color:"#8B8FA3"}}>Accumulated Amortization</td>
                {allMonths.map((m,i)=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"9px",color:isForecast(m)?"#8B8FA3":"#A0A3B8",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(amort[m]?.accumulated||0)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{fontSize:"11px",color:"#B7950B",marginTop:"12px",maxWidth:"680px"}}>Historical (closed) months are a derived straight-line schedule computed from the asset records and may not tie to booked GL actuals — the booked amortization in the Income Statement governs for closed months. Only the forecast months feed the model.</div>
      </div>

      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"16px"}}>
          <div>
            <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E"}}>Intangible Assets — Amortization Schedule</div>
            <div style={{fontSize:"12px",color:"#8B8FA3"}}>Straight-line: Cost Basis ÷ (Useful Life × 12). Click any row to edit.</div>
          </div>
          <ViewOnly canEdit={canEditModel}><button onClick={()=>setModal("new-amort")} style={{padding:"9px 22px",borderRadius:"10px",border:"none",background:"linear-gradient(135deg,#8B6FC0,#6B4FA0)",color:"#fff",cursor:"pointer",fontSize:"13px",fontWeight:700,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 20px rgba(139,111,192,0.25)"}}>+ Add Intangible Asset</button></ViewOnly>
        </div>
        <AssetTable assets={intangAssets} kind="amort" label="Amort." onEdit={a=>setModal({asset:a,kind:"amort"})} cutoff={cutoff} bsLabels={bsLabelMap} startConvention={daStart}/>
      </div>
    </div>}

    {/* ═══ IS FEED (SUMMARY) TAB ═══ */}
    {view==="summary"&&<div>
      <div style={{background:"#FFFFFF",border:"1px solid rgba(0,0,0,0.07)",borderRadius:"16px",padding:"24px"}}>
        <div style={{fontSize:"16px",fontWeight:700,color:"#1A1C2E",marginBottom:"4px"}}>D&amp;A Schedule — Forecast feeds the Income Statement</div>
        <div style={{fontSize:"12px",color:"#8B8FA3",marginBottom:"20px"}}>Asset-derived Depreciation + Amortization. Only the forecast months flow into the Income Statement; closed months are a derived schedule shown for continuity and are superseded by the booked GL actuals the IS uses.</div>

        <div style={{overflowX:"auto"}}>
          <table style={{borderCollapse:"collapse",fontSize:"10px",minWidth:"100%"}}>
            <thead>
              <tr>
                <th style={{...SL,padding:"6px 16px",textAlign:"left",fontSize:"10px",fontWeight:700,color:"#1A1C2E"}}>IS Line Item</th>
                {yearGroups.map(({year,months,allActuals},i)=>{const isLast=i===yearGroups.length-1;const drawBoundary=!isLast&&months[months.length-1]===cutoff;return <th key={year} colSpan={months.length} style={{padding:"6px",textAlign:"center",fontSize:"10px",fontWeight:700,color:allActuals?"#A0A3B8":"#3D85C6",background:allActuals?"#FAFAFA":undefined,borderRight:drawBoundary?"2px solid rgba(61,133,198,0.3)":"none"}}>{year}{allActuals?" — ACTUALS (derived)":""}</th>;})}
              </tr>
              <tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)"}}>
                <th style={{...SL,padding:"8px 16px"}}></th>
                {allMonths.map((m,i)=><th key={m} style={{padding:"8px 4px",textAlign:"right",fontSize:"9px",fontWeight:600,color:isForecast(m)?"#1A1C2E":"#2A6DB0",whiteSpace:"nowrap",background:isForecast(m)?"#FFFFFF":"#FAFAFA",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.3)":"none"}}>{formatMonthLabel(m, "shortYear")}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{...SL,padding:"8px 16px",fontSize:"11px",color:"#3D85C6",fontWeight:600}}>Depreciation</td>
                {allMonths.map((m,i)=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"10px",color:isForecast(m)?"#1A1C2E":"#2A6DB0",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(dep[m]?.expense||0)}</td>)}
              </tr>
              <tr style={{borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                <td style={{...SL,padding:"8px 16px",fontSize:"11px",color:"#8B6FC0",fontWeight:600}}>Amortization</td>
                {allMonths.map((m,i)=><td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"10px",color:isForecast(m)?"#1A1C2E":"#2A6DB0",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(amort[m]?.expense||0)}</td>)}
              </tr>
              <tr style={{borderTop:"2px solid rgba(0,0,0,0.08)"}}>
                <td style={{...SL,padding:"10px 16px",fontWeight:700,fontSize:"12px",color:"#1A1C2E",background:"#FAFAFA"}}>Total D&A</td>
                {allMonths.map((m,i)=>{
                  const total = r2((dep[m]?.expense||0)+(amort[m]?.expense||0));
                  return <td key={m} style={{padding:"10px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:"10px",color:isForecast(m)?"#1A1C2E":"#2A6DB0",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(total)}</td>;
                })}
              </tr>
              <tr style={{borderTop:"1px solid rgba(0,0,0,0.04)"}}>
                <td style={{...SL,padding:"8px 16px",fontSize:"10px",color:"#8B8FA3"}}>Annualized D&A</td>
                {allMonths.map((m,i)=>{
                  const total = r2(r2((dep[m]?.expense||0)+(amort[m]?.expense||0))*12);
                  return <td key={m} style={{padding:"8px 4px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontSize:"9px",color:isForecast(m)?"#8B8FA3":"#A0A3B8",borderRight:i===cutoffIdx?"2px solid rgba(61,133,198,0.08)":"none"}}>{fmtK(total)}</td>;
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Annual Summary */}
        <div style={{marginTop:"24px",padding:"16px",borderRadius:"12px",background:"rgba(0,0,0,0.02)",border:"1px solid rgba(0,0,0,0.05)"}}>
          <div style={{fontSize:"12px",fontWeight:700,color:"#1A1C2E",marginBottom:"12px"}}>Annual Summary</div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:"12px"}}>
            <thead><tr style={{borderBottom:"2px solid rgba(0,0,0,0.08)"}}>
              <th style={{padding:"8px",textAlign:"left",fontSize:"10px",fontWeight:600,color:"#8B8FA3"}}>Line Item</th>
              {yearGroups.map(({year,allActuals})=><th key={year} style={{padding:"8px",textAlign:"right",fontSize:"10px",fontWeight:600,color:allActuals?"#2A6DB0":"#1A1C2E"}}>{year}{allActuals?" (Actual)":" (Forecast)"}</th>)}
            </tr></thead>
            <tbody>
              {[
                ["Depreciation","#3D85C6",months=>months.reduce((s,m)=>r2(s+(dep[m]?.expense||0)),0)],
                ["Amortization","#8B6FC0",months=>months.reduce((s,m)=>r2(s+(amort[m]?.expense||0)),0)],
              ].map(([label,color,fn])=>
                <tr key={label} style={{borderBottom:"1px solid rgba(0,0,0,0.04)"}}>
                  <td style={{padding:"8px",fontWeight:600,color}}>{label}</td>
                  {yearGroups.map(({year,months,allActuals})=><td key={year} style={{padding:"8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:600,color:allActuals?"#2A6DB0":"#1A1C2E"}}>{fmtK(r2(fn(months)))}</td>)}
                </tr>
              )}
              <tr style={{borderTop:"2px solid rgba(0,0,0,0.08)",background:"rgba(0,0,0,0.015)"}}>
                <td style={{padding:"10px 8px",fontWeight:700}}>Total D&A</td>
                {yearGroups.map(({year,months,allActuals})=>{
                  const total = r2(months.reduce((s,m)=>r2(s+(dep[m]?.expense||0)+(amort[m]?.expense||0)),0));
                  return <td key={year} style={{padding:"10px 8px",textAlign:"right",fontFamily:"'Space Mono',monospace",fontWeight:700,color:allActuals?"#2A6DB0":"#1A1C2E"}}>{fmtK(total)}</td>;
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>}

    {/* ═══ MODAL ═══ */}
    {modal==="new-dep" && <AssetModal canEdit={canEditModel} startConvention={daStart} kind="dep" depBsLines={bsLineOptions.fa} amortBsLines={bsLineOptions.ia} onSave={saveDep} onClose={()=>setModal(null)} onDelete={delDep}/>}
    {modal==="new-amort" && <AssetModal canEdit={canEditModel} startConvention={daStart} kind="amort" depBsLines={bsLineOptions.fa} amortBsLines={bsLineOptions.ia} onSave={saveAmort} onClose={()=>setModal(null)} onDelete={delAmort}/>}
    {modal && typeof modal === "object" && modal.kind==="dep" && <AssetModal canEdit={canEditModel} startConvention={daStart} asset={modal.asset} kind="dep" depBsLines={bsLineOptions.fa} amortBsLines={bsLineOptions.ia} onSave={saveDep} onClose={()=>setModal(null)} onDelete={delDep}/>}
    {modal && typeof modal === "object" && modal.kind==="amort" && <AssetModal canEdit={canEditModel} startConvention={daStart} asset={modal.asset} kind="amort" depBsLines={bsLineOptions.fa} amortBsLines={bsLineOptions.ia} onSave={saveAmort} onClose={()=>setModal(null)} onDelete={delAmort}/>}
  </div>;
}
